import {
  QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { DboOnboardingStatusResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { getCacheGroupQueryKey } from 'hooks/queries/dbo/useCacheGroupQuery';
import { getCacheGroupsQueryKey } from 'hooks/queries/dbo/useCacheGroupsQuery';
import { DBO_ACCOUNTS_QUERY_KEY } from 'hooks/queries/dbo/useDbAccountsQuery';
import { getDbComponentsQueryKey } from 'hooks/queries/dbo/useDbComponentsQuery';
import { getDbInstanceDetailsQueryKey } from 'hooks/queries/dbo/useDbInstanceDetailsQuery';

const DBO_REGISTRATION_STATUS_QUERY_KEY = 'dbo-registration-status';

const getDboRegistrationStatusQueryKey = ({
  registrationId,
}: { registrationId?: string } = {}) =>
  [DBO_REGISTRATION_STATUS_QUERY_KEY, registrationId] as const;

type DboRegistrationStatusQueryArgs = {
  registrationId?: string;
  cacheGroupId?: string;
};

const getDboRegistrationStatusQueryOptions = ({
  registrationId,
  cacheGroupId,
  queryClient,
}: {
  registrationId?: string;
  cacheGroupId?: string;
  queryClient: QueryClient;
}) =>
  queryOptions({
    queryKey: getDboRegistrationStatusQueryKey({ registrationId }),
    queryFn: () =>
      apiClient.dbo
        .getRegistrationStatus({
          registrationId: registrationId!,
        })
        .then(({ data }) => {
          const previous =
            queryClient.getQueryData<DboOnboardingStatusResponse>(
              getDboRegistrationStatusQueryKey({ registrationId })
            );
          if (data.status === 'Success' && previous?.status !== 'Success') {
            queryClient.invalidateQueries({
              queryKey: getCacheGroupsQueryKey(),
            });
            queryClient.invalidateQueries({
              queryKey: getDbInstanceDetailsQueryKey(),
            });
            queryClient.invalidateQueries({
              queryKey: getDbComponentsQueryKey(),
            });
            queryClient.invalidateQueries({
              predicate: (query) =>
                query.queryKey[0] === DBO_ACCOUNTS_QUERY_KEY,
            });
            if (cacheGroupId) {
              queryClient.invalidateQueries({
                queryKey: getCacheGroupQueryKey({ id: cacheGroupId }),
              });
            }
          }
          return data;
        }),
    enabled: !!registrationId,
    refetchInterval: 5000,
  });

export const useDboRegistrationStatusQuery = ({
  registrationId,
  cacheGroupId,
}: DboRegistrationStatusQueryArgs) => {
  const queryClient = useQueryClient();

  return useQuery(
    getDboRegistrationStatusQueryOptions({
      registrationId,
      cacheGroupId,
      queryClient,
    })
  );
};
